import { computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import genToken from "@utils/generateToken";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMGroupsStore } from "@stores/mGroups";

export const useMGroupStore = defineStore("mGroup", () => {
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);

  const mGroupsStore = useMGroupsStore();
  const { _selectedMGroup } = storeToRefs(mGroupsStore);

  const mGroup = computed(() => _selectedMGroup.value);

  const activeAudiences = computed(() => currentMCampaign.value.m_audiences.filter((a) => a.status === "active"));

  const activeCreatives = computed(() => currentMCampaign.value.m_creatives.filter((a) => a.status === "active"));

  const currentMAds = computed(() => currentMCampaign.value.m_ads.filter((a) => a.group_token === mGroup.value.token));

  const selectedAudiences = computed(() =>
    activeAudiences.value.filter((a) => mGroup.value.selected_audiences?.includes(a.token) || false),
  );

  const selectedCreatives = computed(() =>
    activeCreatives.value.filter((c) => mGroup.value.selected_creatives?.includes(c.token) || false),
  );

  const isAdsSelected = (aToken, cToken) => {
    let ads = currentMAds.value;

    if (aToken === null) {
      ads = ads.filter((a) => mGroup.value.selected_audiences.includes(a.m_audience_token));
      ads = ads.filter((a) => a.m_creative_token === cToken);
      if (ads.length < selectedAudiences.value.length) return false;
    } else if (cToken === null) {
      ads = ads.filter((a) => mGroup.value.selected_creatives.includes(a.m_creative_token));
      ads = ads.filter((a) => a.m_audience_token === aToken);
      if (ads.length < selectedCreatives.value.length) return false;
    } else {
      ads = ads.filter((a) => a.m_audience_token === aToken && a.m_creative_token === cToken);
    }
    if (ads.length < 1) return false;

    return ads.every((a) => a.status === "active");
  };

  const areSomeSelected = (aToken, cToken) => {
    let ads = currentMAds.value;

    if (aToken === null) {
      ads = ads.filter((a) => mGroup.value.selected_audiences.includes(a.m_audience_token));
      ads = ads.filter((a) => a.m_creative_token === cToken);
      const active = ads.some((a) => a.status === "active");
      const allActive = ads.every((a) => a.status === "active");
      return active && (!allActive || ads.length < selectedAudiences.value.length);
    } else if (cToken === null) {
      ads = ads.filter((a) => mGroup.value.selected_creatives.includes(a.m_creative_token));
      ads = ads.filter((a) => a.m_audience_token === aToken);
      const active = ads.some((a) => a.status === "active");
      const allActive = ads.every((a) => a.status === "active");
      return active && (!allActive || ads.length < selectedCreatives.value.length);
    }
    return false;
  };

  const selectAds = (value, aToken, cToken) => {
    if (aToken === null) {
      selectedAudiences.value.forEach((a) => selectAds(value, a.token, cToken));
    } else if (cToken === null) {
      selectedCreatives.value.forEach((c) => selectAds(value, aToken, c.token));
    } else {
      const newStatus = value === true ? "active" : "inactive";
      const ads = currentMAds.value.filter((a) => a.m_audience_token === aToken && a.m_creative_token === cToken);

      if (ads.length < 1 && newStatus === "active") {
        const newAd = {
          token: genToken(),
          status: newStatus,
          group_token: mGroup.value.token,
          m_creative_token: cToken,
          m_audience_token: aToken,
        };
        currentMCampaign.value.m_ads.push(newAd);
      } else {
        ads.forEach((a) => (a.status = newStatus));
      }
    }
  };

  const clearAds = () => {
    const ads = currentMAds.value;
    const audiences = mGroup.value.selected_audiences;
    const creatives = mGroup.value.selected_creatives;

    ads
      .filter((a) => a.status === "active")
      .forEach((a) => {
        if (!audiences.includes(a.m_audience_token) || !creatives.includes(a.m_creative_token)) a.status = "inactive";
      });
  };

  return {
    mGroup,

    activeAudiences,
    activeCreatives,
    currentMAds,

    selectedAudiences,
    selectedCreatives,

    isAdsSelected,
    areSomeSelected,
    selectAds,
    clearAds,
  };
});
